import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getSdk } from "../sdk";

export type UseLogoutOptions = {
  /**
   * Path or URL to redirect to after logout completes.
   * Defaults to "/login" on the current origin.
   */
  redirectTo?: string;
  /**
   * Optional hook to clear the host app's in-memory access token cache
   * after the API logout call completes (and before redirect).
   */
  onCleared?: () => void;
};

/**
 * Hook that:
 *  1. Awaits the API logout call (which clears the refresh cookie).
 *  2. Lets the host clear its in-memory access token.
 *  3. Clears React Query cache.
 *  4. Hard-navigates to `redirectTo`.
 *
 * Pass `redirectTo` per app: SPA login pages now live on each subdomain
 * (tuyendung.chotca.com/login, admin.chotca.com/login).
 */
export function useLogout(options: UseLogoutOptions | string = {}) {
  const opts: UseLogoutOptions =
    typeof options === "string" ? { redirectTo: options } : options;
  const redirectTo = opts.redirectTo ?? "/login";
  const onCleared = opts.onCleared;
  const qc = useQueryClient();
  return useCallback(async () => {
    try {
      await getSdk().auth.logout();
    } catch {
      // best-effort — still proceed with local cleanup
    }
    if (onCleared) {
      try {
        onCleared();
      } catch {
        // ignore
      }
    }
    qc.clear();
    if (typeof window !== "undefined") {
      window.location.assign(redirectTo);
    }
  }, [qc, redirectTo, onCleared]);
}
